import React, { useState, useEffect } from 'react'

import { useFormik } from 'formik'
import { isEqual } from 'lodash'

import * as S from './styles'
import Table from 'presentation/shared/components/Table'
import { Cid } from 'domain/entities/cid'
import { Tuss } from 'domain/entities/tuss'
import Error from 'presentation/shared/components/Error'
import { toast } from 'react-toastify'
import { makeLocalStorageAdapter } from 'main/factories/cache/local-storage-adapter-factory'
import { useAddSurgicalOrderFunctions } from 'presentation/hospital/pages/AddSurgicalOrder/load-surgical-order'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'

type Props = {
  selectedProcedures: any[]
  setSelectedProcedures?: (procedure: any) => void
  selectedCids: any[]
  setSelectedCids?: (cid: any) => void
  cid?: any[]
  setCid: (procedure: any) => void
  cidList?: Cid[]
  tussList?: Tuss[]
  doctorName?: string
  onlyVisualization?: boolean
}

export const TussTable = ({
  cidList,
  tussList,
  doctorName,
  onlyVisualization,
  selectedProcedures,
  setSelectedProcedures,
  selectedCids,
  setSelectedCids
}: Props) => {
  const [procedures, setProcedure] = useState({} as any)
  const [selectedCid, setSelectedCid] = useState({} as any)
  const [tussQuery, setTussQuery] = useState<string>()
  const [cidQuery, setCidQuery] = useState<string>()

  const context = useAddSurgicalOrderFunctions()

  useEffect(() => {
    const favorite: {
      procedure: {
        doctor_name: string
        procedure_code: string
        isMain: string
        quantity: string
        pro_fat_id: string
        description: string
        surgery_id: string
      }[]

      cid: {
        code: string
        description: string
      }[]
    } = makeLocalStorageAdapter().get('favorite')

    if (favorite && Object.keys(favorite).length > 0) {
      const formattedFavorite: {
        doctor: string
        tuss_id: string
        principalProcedure: string
        quantity: string
        pro_fat_id: string
        description: string
        surgery_id: string
      }[] = favorite.procedure.map(
        ({
          doctor_name: doctor,
          procedure_code: tuss_id,
          isMain: principalProcedure,
          ...favorite
        }) => ({
          doctor,
          tuss_id,
          principalProcedure,
          ...favorite
        })
      )

      formattedFavorite.forEach((favorite) => {
        favorite.principalProcedure = favorite.principalProcedure
          ? 'Procedimento Principal'
          : ''
      })

      setSelectedProcedures && setSelectedProcedures(formattedFavorite)

      const formattedCid: {
        cid_id: string
        description: string
      }[] = favorite.cid.map(({ code: cid_id, ...cid }) => ({
        cid_id,
        ...cid
      }))

      setSelectedCids && setSelectedCids(formattedCid)
    }
  }, [])

  const formik = useFormik({
    initialValues: {
      quantity: 0
    },
    onSubmit: (values) => {
      if (procedures?.tuss_id) {
        setSelectedProcedures &&
          setSelectedProcedures([
            ...(selectedProcedures ?? []),
            {
              tuss_id: procedures.tuss_id,
              description: procedures.description,
              principalProcedure: procedures.principalProcedure,
              quantity: Number(values.quantity),
              surgery_id: procedures.surgery_id,
              doctor: doctorName,
              pro_fat_id: procedures.pro_fat_id
            }
          ])
      }
      setProcedure(null)
      setTussQuery('')
      formik.resetForm()
    }
  })

  const addProcedure = (procedure: any) => {
    const newProcedure = Object.assign({}, procedure)
    if (selectedProcedures.length === 0) {
      newProcedure.principalProcedure = 'Procedimento Principal'
    }
    if (selectedProcedures.find((val) => val.tuss_id === procedure.tuss_id)) {
      toast.error('Procedimento já adicionado')
    } else {
      setTussQuery(procedure.tuss_id)
      setProcedure({ ...newProcedure, description: procedure.description })
    }
  }

  const addCid = (procedure: any) => {
    if (selectedCids?.find((val) => val.cid_id === procedure.cid_id)) {
      return toast.error('CID já inserido')
    }
    setCidQuery(procedure.cid_id)
    setSelectedCid(procedure)
  }

  const submitCid = () => {
    if (selectedCid?.cid_id) {
      setSelectedCids &&
        setSelectedCids([
          ...(selectedCids ?? []),
          {
            cid_id: selectedCid.cid_id,
            description: selectedCid.description
          }
        ])
    }
    setSelectedCid(null)
    setCidQuery('')
  }

  const onDeleteRow = (index: number[]) => {
    setSelectedProcedures &&
      setSelectedProcedures(
        selectedProcedures
          ?.filter(
            (procedure, procedureIndex) => !index.includes(procedureIndex)
          )
          .map((procedures, index) => {
            if (index === 0) {
              return {
                ...procedures,
                principalProcedure: 'Procedimento Principal'
              }
            } else {
              return { ...procedures }
            }
          })
      )
  }

  const onDeleteCid = (index: number[]) => {
    setSelectedCids &&
      setSelectedCids(
        selectedCids?.filter(
          (procedure, procedureIndex) => !index.includes(procedureIndex)
        )
      )
  }

  const isValid = () => {
    return !!(
      formik.values.quantity > 0 &&
      procedures?.tuss_id &&
      procedures?.description
    )
  }

  const resetCidValues = () => {
    setSelectedCid(undefined)
    setCidQuery('')
  }

  const resetProceduresValues = () => {
    setProcedure(undefined)
    setTussQuery('')
  }

  return (
    <S.Wrapper>
      {!onlyVisualization && (
        <S.Inputs>
          <S.InputButtonContainer>
            <AutoCompleteNew
              label="Cód. Tuss"
              placeholder="TUSS"
              value={tussQuery}
              onSuggestionClick={addProcedure}
              style={{ width: '100%' }}
              onType={(val) => context?.loadSurgeries?.(1, 5, val)}
              onInputChange={setTussQuery}
              suggestions={tussList?.map((tuss) => ({
                label: tuss.tuss_id + ' - ' + tuss.description,
                value: tuss
              }))}
              disabled={!!procedures?.tuss_id}
              clear={() => !!procedures?.tuss_id && resetProceduresValues()}
              iconPosition="insideRight"
              iconIsButton
              required
              data-testid="select-tuss-autocomplete"
            />
          </S.InputButtonContainer>
          <TextFieldNew
            value={procedures?.description || ''}
            placeholder="Descrição"
            style={{ width: '120%', marginLeft: '12px' }}
            disabled
            data-testid={`tuss-description-disabled-input`}
          />
          <S.QuantityContainer>
            <S.Title style={{ fontSize: '18px' }}>Quantidade</S.Title>
            <TextFieldNew
              onInputChange={formik.handleChange('quantity')}
              value={formik.values.quantity}
              type="number"
              min={0}
              data-testid={`tuss-quantity-input`}
            />
          </S.QuantityContainer>
          <ButtonNew
            size="large"
            type="button"
            disabled={!isValid()}
            onClick={formik.submitForm}
            style={{ maxWidth: '120px' }}
            data-testid={`add-tuss-button`}
          >
            Adicionar
          </ButtonNew>
        </S.Inputs>
      )}
      {selectedProcedures?.length > 0 ? (
        <Table
          checkbox={!onlyVisualization}
          data={selectedProcedures}
          columns={columns}
          rowsDelete={onDeleteRow}
          pagination={false}
        />
      ) : (
        <Error color="red" size="medium">
          Nenhum procedimento selecionado
        </Error>
      )}
      <br />
      {!onlyVisualization && (
        <S.Inputs>
          <S.InputButtonContainer>
            <AutoCompleteNew
              label="CID"
              placeholder="CID"
              value={cidQuery}
              onSuggestionClick={addCid}
              style={{ width: '100%' }}
              onType={(val) => context?.loadCid?.(1, 5, val)}
              onInputChange={setCidQuery}
              suggestions={cidList?.map((cid) => ({
                label: cid.cid_id + ' - ' + cid.description,
                value: cid
              }))}
              disabled={!!selectedCid?.cid_id}
              clear={() => !!selectedCid?.cid_id && resetCidValues()}
              iconPosition="insideRight"
              iconIsButton
              required
              data-testid="select-cid-autocomplete"
            />
          </S.InputButtonContainer>
          <TextFieldNew
            value={selectedCid?.description || ''}
            placeholder="Descrição"
            style={{ width: '160%', marginLeft: '12px', marginRight: '18px' }}
            disabled
            data-testid={`cid-description-disabled-input`}
          />
          <ButtonNew
            size="large"
            type="button"
            disabled={!selectedCid?.cid_id}
            onClick={submitCid}
            style={{ maxWidth: '120px' }}
            data-testid={`add-cid-button`}
          >
            Adicionar
          </ButtonNew>
        </S.Inputs>
      )}
      {selectedCids?.length > 0 ? (
        <Table
          checkbox={!onlyVisualization}
          data={selectedCids}
          columns={cidColumns}
          rowsDelete={onDeleteCid}
          pagination={false}
        />
      ) : (
        <Error color="red" size="medium">
          Nenhum CID selecionado
        </Error>
      )}
    </S.Wrapper>
  )
}

function dataCompare(prevData: Props, nextData: Props) {
  const prev = {
    cids: prevData.cidList,
    tuss: prevData.tussList,
    selectedTuss: prevData.selectedProcedures,
    selectedCids: prevData.cid
  }

  const next = {
    cids: nextData.cidList,
    tuss: nextData.tussList,
    selectedTuss: nextData.selectedProcedures,
    selectedCids: nextData.cid
  }
  return isEqual(prev, next)
}

export default React.memo(TussTable, dataCompare)

const columns = [
  {
    label: 'Procedimento principal',
    name: 'principalProcedure',
    options: {
      sort: false
    }
  },
  {
    label: 'Código',
    name: 'tuss_id',
    options: {
      sort: false
    }
  },
  {
    label: 'Descrição',
    name: 'description',
    options: {
      sort: false
    }
  },
  {
    label: 'QT',
    name: 'quantity',
    options: {
      sort: false
    }
  },
  {
    label: 'Médico',
    name: 'doctor',
    options: {
      sort: false
    }
  }
]

const cidColumns = [
  {
    label: 'Código',
    name: 'cid_id',
    options: {
      sort: false
    }
  },
  {
    label: 'Descrição',
    name: 'description',
    options: {
      sort: false
    }
  }
]
